<template>
  <div class="app-banners-stack" :class="{ 'with-header': withHeader }">
    <!-- Version Update Banner -->
    <div 
      v-if="needRefresh" 
      class="update-banner" 
      :class="{ 'with-header': withHeader }" 
      @click="reloadApp"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" class="update-banner-icon">
        <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
      </svg>
      <span class="update-banner-text">
        <span class="text-long">Es gibt ein Update – App neu laden</span>
        <span class="text-short">Update verfügbar – Neu laden</span>
      </span>
    </div>

    <!-- Global Admin Announcement Banner -->
    <GlobalAnnouncementBanner
      :with-header="withHeader && !needRefresh"
    />

    <!-- Offline Status Banner -->
    <div 
      v-if="effectiveOffline" 
      class="offline-status-banner"
      :class="{
        'with-header': withHeader && !needRefresh && !currentBannerAnnouncement,
        'is-manual': isManualOffline,
        'is-liefi': isLieFi
      }"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" class="offline-status-icon">
        <path d="M23.64 7c-.45-.34-4.93-3.6-11.64-3.6-1.5 0-2.89.19-4.15.48L18.18 14.2 23.64 7zm-6.6 8.22L3.27 1.44 2 2.72l2.05 2.06C1.91 5.76.59 6.82.36 7l11.63 14.49.01.01.01-.01 3.9-4.86 3.32 3.32 1.27-1.27-3.46-3.46z"/>
      </svg>

      <span class="offline-status-text">
        <template v-if="isManualOffline">
          <span class="text-long">Offline-Modus aktiv (Manuell)</span>
          <span class="text-short">Offline (Manuell)</span>
        </template>
        <template v-else-if="isLieFi">
          <span class="text-long">Schlechtes Internet – Offline</span>
          <span class="text-short">Schlechtes Netz</span>
        </template>
        <template v-else>
          <span class="text-long">Offline – Gespeicherte Daten verfügbar</span>
          <span class="text-short">Offline-Modus</span>
        </template>
      </span>

      <button 
        v-if="isManualOffline" 
        type="button" 
        class="offline-action-btn"
        @click="setManualOffline(false)"
      >
        <span class="btn-text-long">Online gehen</span>
        <span class="btn-text-short">Online</span>
      </button>
      <button 
        v-else-if="isLieFi" 
        type="button" 
        class="offline-action-btn"
        @click="() => checkConnection()"
      >
        Prüfen
      </button>
    </div>

    <!-- Public Demo Mode Banner -->
    <DemoBanner 
      :with-header="withHeader && !needRefresh && !currentBannerAnnouncement && !effectiveOffline" 
      @update:visible="isDemoBannerVisible = $event" 
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { usePwaUpdate } from '../../composables/usePwaUpdate'
import { useNetworkState } from '../../composables/useNetworkState'
import { useAnnouncements } from '../../composables/useAnnouncements'
import DemoBanner from './DemoBanner.vue'
import GlobalAnnouncementBanner from './GlobalAnnouncementBanner.vue'

withDefaults(defineProps<{
  withHeader?: boolean
}>(), {
  withHeader: false
})

const emit = defineEmits<{
  (e: 'update:active', active: boolean): void
}>()

const { needRefresh, reloadApp } = usePwaUpdate()
const {
  effectiveOffline,
  isManualOffline,
  isLieFi,
  setManualOffline,
  checkConnection
} = useNetworkState()
const { currentBannerAnnouncement, initializeAnnouncementsListener } = useAnnouncements()

const isDemoBannerVisible = ref(false)

const hasActiveBanner = computed(() => {
  return Boolean(needRefresh.value) ||
         Boolean(currentBannerAnnouncement.value) ||
         Boolean(effectiveOffline.value) ||
         Boolean(isDemoBannerVisible.value)
})

watch(hasActiveBanner, (val) => {
  emit('update:active', val)
}, { immediate: true })

onMounted(() => {
  initializeAnnouncementsListener()
  emit('update:active', hasActiveBanner.value)
})
</script>

<style scoped>
.app-banners-stack {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
  z-index: 15;
}

/* Update Banner */
.update-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: #8bc34a;
  color: white;
  padding: 6px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  animation: pulse-update 2s infinite ease-in-out;
  flex-shrink: 0;
  z-index: 25;
  position: relative;
  flex-wrap: nowrap;
  white-space: nowrap;
  overflow: hidden;
  line-height: 1.2;
}

.update-banner.with-header {
  margin-top: 56px;
}

.update-banner-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

@keyframes pulse-update {
  0%, 100% { background-color: #8bc34a; }
  50% { background-color: #7cb342; }
}

/* Offline Status Banner */
.offline-status-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background-color: #ff9800;
  color: #fff;
  padding: 5px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  z-index: 15;
  position: relative;
  flex-wrap: nowrap;
  white-space: nowrap;
  overflow: hidden;
  line-height: 1.2;
}

.offline-status-banner.is-liefi {
  background-color: #f57c00;
}

.offline-status-banner.is-manual {
  background-color: #546e7a;
}

.offline-status-banner.with-header {
  margin-top: 56px;
}

.offline-status-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.offline-status-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-short {
  display: none;
}
.text-long {
  display: inline;
}

.btn-text-short {
  display: none;
}
.btn-text-long {
  display: inline;
}

@media (max-width: 540px) {
  .offline-status-banner {
    padding: 4px 8px;
    font-size: 0.76rem;
    gap: 0.35rem;
  }

  .update-banner {
    padding: 5px 8px;
    font-size: 0.78rem;
    gap: 0.35rem;
  }

  .text-long {
    display: none;
  }
  .text-short {
    display: inline;
  }

  .btn-text-long {
    display: none;
  }
  .btn-text-short {
    display: inline;
  }
}

.offline-action-btn {
  margin-left: 0.25rem;
  padding: 2px 7px;
  background: rgba(255, 255, 255, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  color: #fff;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.offline-action-btn:hover {
  background: rgba(255, 255, 255, 0.38);
}
</style>
